import { useEffect, useRef, useState } from "react";

export default function FeaturesSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="features" ref={ref} className="bg-[#0F0A1E] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="text-xs font-semibold text-[#C8B6FF] uppercase tracking-widest mb-3">Features</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            Everything you need to{" "}
            <span className="text-[#C8B6FF]">connect for real</span>
          </h2>
        </div>

        {/* Feature Grid */}
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-5 transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* Feature 1 — Large */}
          <div className="md:row-span-2 bg-gradient-to-br from-[#7C55CC] to-[#4A2D8A] rounded-3xl p-8 md:p-10 flex flex-col gap-5 hover:-translate-y-1 transition-transform duration-300 cursor-default">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-white/15">
              <i className="ri-sparkling-2-line text-2xl text-white"></i>
            </div>
            <div>
              <span className="text-xs font-semibold text-[#C8B6FF]/70 uppercase tracking-widest">Feature 01</span>
              <h3 className="text-2xl font-bold text-white mt-1 mb-3">AI Swiping Algorithm</h3>
              <p className="text-white/70 text-sm leading-relaxed">
                Matches you with people who actually fit — not just look good. Learns from your interests, personality, and behaviour over time.
              </p>
            </div>
            <ul className="flex flex-col gap-2 mt-2">
              {["Learns your interests", "Personality matching", "Behaviour signals", "Less swipe fatigue"].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-white/80">
                  <i className="ri-check-line text-[#C8B6FF]"></i>
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-auto">
              <span className="inline-block bg-white/10 text-white text-xs font-medium px-4 py-1.5 rounded-full">
                Higher match quality
              </span>
            </div>
          </div>

          {/* Feature 2 */}
          <div className="bg-[#1A1030] rounded-3xl p-8 flex flex-col gap-4 hover:-translate-y-1 transition-transform duration-300 cursor-default">
            <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-[#C8B6FF]/15">
              <i className="ri-group-line text-xl text-[#C8B6FF]"></i>
            </div>
            <div>
              <span className="text-xs font-semibold text-[#C8B6FF]/50 uppercase tracking-widest">Feature 02</span>
              <h3 className="text-xl font-bold text-white mt-1 mb-2">Interest-Based Communities</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Meet people naturally before romantic intent. Join hobby groups, browse profiles, and like without pressure.
              </p>
            </div>
            <span className="inline-block bg-[#C8B6FF]/10 text-[#C8B6FF] text-xs font-medium px-4 py-1.5 rounded-full w-fit">
              No awkward openers
            </span>
          </div>

          {/* Feature 3 */}
          <div className="bg-[#3D2080] rounded-3xl p-8 flex flex-col gap-4 hover:-translate-y-1 transition-transform duration-300 cursor-default">
            <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white/15">
              <i className="ri-calendar-event-line text-xl text-white"></i>
            </div>
            <div>
              <span className="text-xs font-semibold text-white/40 uppercase tracking-widest">Feature 03</span>
              <h3 className="text-xl font-bold text-white mt-1 mb-2">Host &amp; Join Events</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Create your own dates, join curated events, make real-life connections. Less texting, more real moments.
              </p>
            </div>
            <span className="inline-block bg-white/10 text-white text-xs font-medium px-4 py-1.5 rounded-full w-fit">
              Real-life meetings
            </span>
          </div>

          {/* Feature 4 — Full width */}
          <div className="md:col-span-2 bg-[#1A1030] border border-white/10 rounded-3xl p-8 flex flex-col md:flex-row items-start md:items-center gap-8 hover:-translate-y-1 transition-transform duration-300 cursor-default">
            <div className="flex-1">
              <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-[#C8B6FF]/15 mb-4">
                <i className="ri-star-line text-xl text-[#C8B6FF]"></i>
              </div>
              <span className="text-xs font-semibold text-[#C8B6FF]/50 uppercase tracking-widest">Feature 04</span>
              <h3 className="text-xl font-bold text-white mt-1 mb-2">Reputation &amp; Safety System</h3>
              <p className="text-white/60 text-sm leading-relaxed max-w-md">
                A 1–5 star behaviour-based trust system. Unlock features as your reputation grows. Safer community, more accountability.
              </p>
            </div>
            <div className="flex flex-col gap-3 md:min-w-[240px]">
              {[
                { stars: "3★", label: "1-on-1 events", color: "bg-[#C8B6FF]/20 text-[#C8B6FF]" },
                { stars: "4★", label: "Group events", color: "bg-[#9B72FF]/30 text-[#D4B8FF]" },
                { stars: "5★", label: "Verified badge ✓", color: "bg-[#7C55CC]/40 text-white" },
              ].map((tier) => (
                <div key={tier.stars} className="flex items-center gap-3 bg-white/5 rounded-2xl px-5 py-3">
                  <span className="text-[#C8B6FF] font-bold w-8">{tier.stars}</span>
                  <span className="text-sm text-white/80">Unlocks:</span>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${tier.color}`}>{tier.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
